import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Trash2, Plus, Minus, ShoppingCart, User, Trophy, Users, DollarSign } from 'lucide-react';
import { formatDuree, formatDureeFull, getDureeSubLabel } from '../utils/formatDuration';
import { notifyDataChanged } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Vendre = () => {
  const { user } = useAuth();
  const [rubriques, setRubriques] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedRubrique, setSelectedRubrique] = useState(null);
  const [panier, setPanier] = useState([]);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [client, setClient] = useState({ nom: '', prenom: '', telephone: '', jeux: '' });
  const [modePaiement, setModePaiement] = useState('Especes');
  const [loading, setLoading] = useState(false);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [jeux, setJeux] = useState([]);
  
  // Tournament states
  const [tournoiFormData, setTournoiFormData] = useState({
    nomTournoi: '',
    nombreJoueurs: '',
    participation: ''
  });
  const [tournoiCommercial, setTournoiCommercial] = useState(null);
  const [tournoiLoading, setTournoiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('vente'); // 'vente' ou 'tournoi'

  const selectedRubriqueRef = React.useRef(selectedRubrique);
  selectedRubriqueRef.current = selectedRubrique;

  useEffect(() => {
    fetchRubriques();
    fetchCommercials();
    fetchJeux();
    
    // Listen for pricing updates from Admin (cross-tab via storage event)
    const handleStorageChange = (e) => {
      if (e.key === 'pricing_updated') {
        const rub = selectedRubriqueRef.current;
        if (rub) fetchArticles(rub.id);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Same-tab polling: check for pricing updates every 3 seconds
    const intervalId = setInterval(() => {
      const lastUpdate = localStorage.getItem('pricing_updated');
      if (lastUpdate) {
        const lastFetch = sessionStorage.getItem('last_pricing_fetch');
        if (!lastFetch || parseInt(lastUpdate) > parseInt(lastFetch)) {
          const rub = selectedRubriqueRef.current;
          if (rub) fetchArticles(rub.id);
          sessionStorage.setItem('last_pricing_fetch', Date.now().toString());
        }
      }
    }, 3000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  const fetchJeux = async () => {
    try {
      const response = await axios.get(`${API}/jeux`, { withCredentials: true });
      // Filtrer seulement les jeux actifs
      const activeJeux = response.data.filter(j => j.actif);
      setJeux(activeJeux);
    } catch (error) {
      console.error('Erreur jeux:', error);
    }
  };

  const fetchCommercials = async () => {
    try {
      const response = await axios.get(`${API}/commerciaux`, { withCredentials: true });
      // Filtrer seulement les commerciaux actifs
      const activeCommercials = response.data.filter(c => c.actif);
      setCommercials(activeCommercials);
    } catch (error) {
      console.error('Erreur commerciaux:', error);
    }
  };

  useEffect(() => {
    if (selectedRubrique && activeTab === 'vente') {
      fetchArticles(selectedRubrique.id);
    }
  }, [selectedRubrique, activeTab]);

  const fetchRubriques = async () => {
    try {
      const response = await axios.get(`${API}/rubriques`, { withCredentials: true });
      const activeRubriques = response.data.filter(r => r.actif && r.nom.toUpperCase() !== 'TOURNOI');
      setRubriques(activeRubriques);
      if (activeRubriques.length > 0) {
        setSelectedRubrique(activeRubriques[0]);
      }
    } catch (error) {
      console.error('Erreur rubriques:', error);
      toast.error('Erreur de chargement des rubriques');
    }
  };

  const fetchArticles = async (rubriqueId) => {
    try {
      const response = await axios.get(`${API}/articles`, {
        params: { rubrique_id: rubriqueId },
        withCredentials: true
      });
      setArticles(response.data.filter(a => a.actif));
    } catch (error) {
      console.error('Erreur articles:', error);
    }
  };

  const handleAddToCart = (article) => {
    if (selectedRubrique.type === 'LIBRE') {
      setCurrentArticle(article);
      setShowPriceDialog(true);
      return;
    }

    if (selectedRubrique.type === 'TEMPS') {
      setCurrentArticle(article);
      setShowPriceDialog(true);
      return;
    }

    // UNITAIRE
    const existingIndex = panier.findIndex(item => item.articleId === article.id);
    if (existingIndex >= 0) {
      const newPanier = [...panier];
      newPanier[existingIndex].qte += 1;
      newPanier[existingIndex].montant = newPanier[existingIndex].qte * newPanier[existingIndex].prixUnitaire;
      setPanier(newPanier);
    } else {
      setPanier([...panier, {
        articleId: article.id,
        rubriqueId: article.rubriqueId,
        libelle: article.nom,
        qte: 1,
        prixUnitaire: article.prixUnit,
        montant: article.prixUnit
      }]);
    }
  };

  const handleConfirmPrice = () => {
    if (selectedRubrique.type === 'LIBRE') {
      const prix = parseInt(customPrice);
      if (!prix || prix <= 0) {
        toast.error('Prix invalide');
        return;
      }
      setPanier([...panier, {
        articleId: currentArticle.id,
        rubriqueId: currentArticle.rubriqueId,
        libelle: currentArticle.nom,
        qte: 1,
        prixUnitaire: prix,
        montant: prix
      }]);
      setShowPriceDialog(false);
      setCustomPrice('');
    } else if (selectedRubrique.type === 'TEMPS') {
      if (!selectedDuration) {
        toast.error('Veuillez sélectionner une durée');
        return;
      }
      // Get price directly from article's prixParDuree (single source of truth)
      const prix = currentArticle.prixParDuree?.[selectedDuration] || 0;
      setPanier([...panier, {
        articleId: currentArticle.id,
        rubriqueId: currentArticle.rubriqueId,
        libelle: `${currentArticle.nom} (${formatDureeFull(selectedDuration)})`,
        qte: 1,
        prixUnitaire: prix,
        montant: prix,
        meta: { duree: selectedDuration }
      }]);
      setShowPriceDialog(false);
      setSelectedDuration(null);
    }
  };

  const updateQuantity = (index, delta) => {
    const newPanier = [...panier];
    newPanier[index].qte += delta;
    if (newPanier[index].qte <= 0) {
      newPanier.splice(index, 1);
    } else {
      newPanier[index].montant = newPanier[index].qte * newPanier[index].prixUnitaire;
    }
    setPanier(newPanier);
  };

  const removeFromCart = (index) => {
    const newPanier = [...panier];
    newPanier.splice(index, 1);
    setPanier(newPanier);
  };

  const calculateTotal = () => {
    return panier.reduce((sum, item) => sum + item.montant, 0);
  };

  const hasJeuxInCart = () => {
    // Check if cart has items from JEUX rubriques (TEMPS type with games)
    return panier.some(item => item.meta && item.meta.duree);
  };

  const handleEncaisser = async () => {
    if (panier.length === 0) {
      toast.error('Panier vide');
      return;
    }

    // Ouvrir le dialog client si infos manquantes
    if (!client.nom || !client.telephone || !selectedCommercial) {
      // RESET COMPLET du client AVANT d'ouvrir le dialog
      setClient({ nom: '', prenom: '', telephone: '', jeux: '' });
      setShowClientDialog(true);
      return;
    }

    setLoading(true);
    try {
      // Calculate CA par rubrique
      const detailsCA = {};
      panier.forEach(item => {
        detailsCA[item.rubriqueId] = (detailsCA[item.rubriqueId] || 0) + item.montant;
      });

      // Find commercial name
      const commercial = commercials.find(c => c.id === selectedCommercial);

      const transactionData = {
        client,
        modePaiement,
        total: calculateTotal(),
        detailsCA,
        items: panier,
        commercialId: selectedCommercial,
        commercialName: commercial?.nom
      };

      const response = await axios.post(`${API}/transactions`, transactionData, { withCredentials: true });
      
      toast.success(`Vente enregistrée ! Reçu N°${response.data.numeroRecu}`);
      notifyDataChanged('transaction');

      // Get receipt image
      const imageResponse = await axios.get(`${API}/transactions/${response.data.id}/image`, { withCredentials: true });
      setReceiptImage(imageResponse.data.image);
      setCurrentTransactionId(response.data.id);

      // Reset cart and client - FORCE COMPLETE RESET
      setPanier([]);
      setClient({ nom: '', prenom: '', telephone: '', jeux: '' });
      setModePaiement('Especes');
      setSelectedCommercial(null);
      setShowClientDialog(false);
      
      // Show receipt dialog
      setShowReceiptDialog(true);
    } catch (error) {
      console.error('Erreur encaissement:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'encaissement');
    } finally {
      setLoading(false);
    }
  };

  // Tournament handlers
  const handleTournoiInputChange = (field, value) => {
    setTournoiFormData({ ...tournoiFormData, [field]: value });
  };

  const calculateTournoiPreview = () => {
    const nombreJoueurs = parseInt(tournoiFormData.nombreJoueurs) || 0;
    const participation = parseInt(tournoiFormData.participation) || 0;
    const total = nombreJoueurs * participation;
    const gainVainqueur = Math.floor(total * 0.4);
    const gainDeuxieme = Math.floor(total * 0.2);
    const gainPlayCity = Math.floor(total * 0.4);

    return { total, gainVainqueur, gainDeuxieme, gainPlayCity };
  };

  const handleTournoiSubmit = async (e) => {
    e.preventDefault();

    if (!tournoiFormData.nomTournoi || !tournoiFormData.nombreJoueurs || !tournoiFormData.participation) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (!tournoiCommercial) {
      toast.error('Veuillez sélectionner l\'organisateur du tournoi');
      return;
    }

    setTournoiLoading(true);
    try {
      const commercial = commercials.find(c => c.id === tournoiCommercial);

      await axios.post(`${API}/tournaments`, {
        nomTournoi: tournoiFormData.nomTournoi,
        nombreJoueurs: parseInt(tournoiFormData.nombreJoueurs),
        participation: parseInt(tournoiFormData.participation),
        commercialId: tournoiCommercial,
        commercialName: commercial?.nom
      }, { withCredentials: true });

      toast.success('Tournoi enregistré avec succès !');
      notifyDataChanged('tournoi');
      
      setTournoiFormData({ nomTournoi: '', nombreJoueurs: '', participation: '' });
      setTournoiCommercial(null);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setTournoiLoading(false);
    }
  };

  const tournoiPreviewData = tournoiFormData.nombreJoueurs && tournoiFormData.participation ? calculateTournoiPreview() : null;

  // Ordre des onglets: JEUX, TOURNOIS, BOISSON, NESPRESSO, CASINO
  const orderedRubriques = () => {
    const order = ['JEUX', 'TOURNOIS', 'BOISSON', 'NESPRESSO', 'CAFE', 'CAFÉ', 'CASINO'];
    const sorted = [...rubriques].sort((a, b) => {
      const indexA = order.indexOf(a.nom.toUpperCase());
      const indexB = order.indexOf(b.nom.toUpperCase());
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
    
    // Insérer TOURNOIS entre JEUX et BOISSON
    const jeuxIndex = sorted.findIndex(r => r.nom.toUpperCase() === 'JEUX');
    if (jeuxIndex !== -1) {
      sorted.splice(jeuxIndex + 1, 0, { id: 'tournoi', nom: 'TOURNOIS', type: 'SPECIAL' });
    } else {
      sorted.unshift({ id: 'tournoi', nom: 'TOURNOIS', type: 'SPECIAL' });
    }
    
    return sorted;
  };

  const filteredArticles = articles;

  return (
    <div className="space-y-6 pb-20 xl:pb-8" data-testid="vendre-page">
      {/* ULTRA PREMIUM HEADER - RESPONSIVE */}
      <div className="relative overflow-hidden group">
        <div className="absolute -inset-4 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] rounded-3xl blur-3xl opacity-30 animate-pulse group-hover:opacity-50 transition-opacity duration-700"></div>
        
        <div className="relative backdrop-blur-2xl bg-gradient-to-br from-[#0B132B]/95 via-[#1C2541]/90 to-[#0B132B]/95 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-2 sm:border-4 shadow-2xl"
          style={{
            borderImage: 'linear-gradient(135deg, #FFD700, #FFA500, #FFD700, #C6A15B) 1',
            boxShadow: '0 20px 80px -20px rgba(255, 215, 0, 0.5)'
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-xl sm:rounded-2xl blur-xl opacity-75"></div>
                <div className="relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#FFD700] via-[#FFA500] to-[#FF8C00] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-110 hover:rotate-6 transition-all duration-500">
                  <ShoppingCart className="w-7 h-7 sm:w-9 sm:h-9 md:w-12 md:h-12 text-[#0B132B]" strokeWidth={3} />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black mb-1 sm:mb-2 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] bg-clip-text text-transparent animate-pulse truncate"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.8))' }}
                >
                  💰 VENTES & PRESTATIONS
                </h1>
                <div className="flex items-center gap-2">
                  <div className="px-2 sm:px-3 md:px-4 py-1 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-full shadow-lg animate-pulse">
                    <p className="text-white font-black text-xs sm:text-sm uppercase">🚀 Cash IN System</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-green-600/20 to-green-500/20 backdrop-blur-xl px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl border-2 border-green-400/40 flex-shrink-0">
              <div className="relative">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full animate-ping"></div>
              </div>
              <span className="text-green-300 font-bold text-sm sm:text-base md:text-lg">EN LIGNE</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Articles et Panier - Layout modifié pour mobile */}
        <div className="space-y-4">
          {/* Tabs Rubriques - Design ULTRA PREMIUM & WAHOOOO */}
          <Tabs value={activeTab === 'tournoi' ? 'tournoi' : selectedRubrique?.id} onValueChange={(value) => {
            if (value === 'tournoi') {
              setActiveTab('tournoi');
            } else {
              setActiveTab('vente');
              const rubrique = rubriques.find(r => r.id === value);
              setSelectedRubrique(rubrique);
            }
          }}>
            {/* ULTRA WAHOOO TABS - OPTIMISÉ MOBILE */}
            <div className="mb-6">
              {/* Container sans scroll horizontal - tout visible */}
              <div className="overflow-visible pb-4">
                <TabsList className="flex flex-wrap justify-center items-stretch gap-2 sm:gap-3 md:gap-6 px-2 sm:px-0 bg-transparent border-0 h-auto p-0">
                  {orderedRubriques().map(rubrique => {
                    const icons = {
                      'JEUX': '🎮',
                      'TOURNOIS': '🏆',
                      'BOISSON': '🥤',
                      'NESPRESSO': '☕',
                      'CAFE': '☕',
                      'CAFÉ': '☕',
                      'CASINO': '🎰'
                    };
                    const colors = {
                      'JEUX': { primary: '#3B82F6', secondary: '#60A5FA', glow: 'rgba(59, 130, 246, 0.5)' },
                      'TOURNOIS': { primary: '#EF4444', secondary: '#F87171', glow: 'rgba(239, 68, 68, 0.5)' },
                      'BOISSON': { primary: '#10B981', secondary: '#34D399', glow: 'rgba(16, 185, 129, 0.5)' },
                      'NESPRESSO': { primary: '#F59E0B', secondary: '#FBBF24', glow: 'rgba(245, 158, 11, 0.5)' },
                      'CAFE': { primary: '#F59E0B', secondary: '#FBBF24', glow: 'rgba(245, 158, 11, 0.5)' },
                      'CAFÉ': { primary: '#F59E0B', secondary: '#FBBF24', glow: 'rgba(245, 158, 11, 0.5)' },
                      'CASINO': { primary: '#8B5CF6', secondary: '#A78BFA', glow: 'rgba(139, 92, 246, 0.5)' }
                    };
                    
                    const tabValue = rubrique.id === 'tournoi' ? 'tournoi' : rubrique.id;
                    const isActive = activeTab === 'tournoi' ? tabValue === 'tournoi' : tabValue === selectedRubrique?.id;
                    
                    const displayName = rubrique.nom.toUpperCase() === 'CAFE' || rubrique.nom.toUpperCase() === 'CAFÉ' 
                      ? 'NESPRESSO' 
                      : rubrique.nom;
                    
                    const colorScheme = colors[displayName] || colors[rubrique.nom] || { primary: '#C6A15B', secondary: '#FFD700', glow: 'rgba(198, 161, 91, 0.5)' };
                    
                    return (
                      <TabsTrigger
                        key={rubrique.id}
                        value={tabValue}
                        data-testid={`tab-${rubrique.nom.toLowerCase()}`}
                        className="relative group p-0 bg-transparent border-0 flex-shrink-0"
                      >
                        {/* ULTRA WAHOOO CARD - TAILLE RÉDUITE MOBILE */}
                        <div className="relative">
                          {/* Mega glow animé quand actif */}
                          {isActive && (
                            <>
                              <div className="absolute -inset-1.5 sm:-inset-3 rounded-xl sm:rounded-3xl blur-lg sm:blur-2xl opacity-60 animate-pulse"
                                style={{ background: `linear-gradient(135deg, ${colorScheme.primary}, ${colorScheme.secondary})` }}
                              ></div>
                              <div className="absolute -inset-0.5 rounded-xl sm:rounded-3xl blur-md opacity-40 animate-ping"
                                style={{ background: colorScheme.primary, animationDuration: '2s' }}
                              ></div>
                            </>
                          )}
                          
                          {/* Card principale - TAILLE MOBILE ULTRA RÉDUITE */}
                          <div className={`
                            relative overflow-hidden rounded-xl sm:rounded-2xl md:rounded-3xl
                            transition-all duration-500 transform
                            w-[65px] h-[75px] sm:w-[120px] sm:h-[130px] md:w-[140px] md:h-[150px]
                            flex flex-col items-center justify-center gap-1 sm:gap-2 md:gap-3
                            ${isActive 
                              ? 'scale-110 -translate-y-1.5 sm:scale-105 sm:-translate-y-3' 
                              : 'hover:scale-105 hover:-translate-y-1'
                            }
                          `}
                          style={{
                            background: isActive 
                              ? `linear-gradient(135deg, ${colorScheme.primary} 0%, ${colorScheme.secondary} 50%, ${colorScheme.primary} 100%)`
                              : 'linear-gradient(135deg, rgba(30, 41, 70, 0.95) 0%, rgba(45, 55, 85, 0.95) 100%)',
                            border: isActive 
                              ? `2px solid rgba(255, 255, 255, 0.4)` 
                              : '1.5px solid rgba(139, 92, 246, 0.3)',
                            boxShadow: isActive 
                              ? `0 15px 50px -15px ${colorScheme.glow}, 0 8px 30px -15px ${colorScheme.glow}, inset 0 -2px 10px rgba(0,0,0,0.4), inset 0 2px 10px rgba(255,255,255,0.3)`
                              : '0 6px 20px rgba(0,0,0,0.5), inset 0 0 12px rgba(139, 92, 246, 0.1)',
                            color: isActive ? 'white' : '#E5E7EB'
                          }}
                          >
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            
                            {/* Particles - Hidden on mobile */}
                            {isActive && (
                              <>
                                <div className="hidden sm:block absolute top-2 left-2 w-1.5 h-1.5 rounded-full bg-white/80 animate-ping"></div>
                                <div className="hidden sm:block absolute top-4 right-3 w-1 h-1 rounded-full bg-white/60 animate-ping" style={{ animationDelay: '0.3s' }}></div>
                              </>
                            )}
                            
                            {/* Icon - TAILLE ULTRA RÉDUITE MOBILE */}
                            <div className={`
                              relative z-10 text-2xl sm:text-4xl md:text-6xl
                              transition-all duration-500
                              ${isActive ? 'scale-110 rotate-12' : 'group-hover:scale-105 group-hover:rotate-6'}
                            `}
                            style={{ 
                              filter: isActive 
                                ? `drop-shadow(0 0 12px rgba(255,255,255,0.9)) drop-shadow(0 0 25px ${colorScheme.primary})`
                                : 'drop-shadow(0 2px 5px rgba(0,0,0,0.6))'
                            }}
                            >
                              {icons[displayName] || icons[rubrique.nom] || '📦'}
                            </div>
                            
                            {/* Nom - TAILLE ULTRA RÉDUITE MOBILE */}
                            <div className="relative z-10 text-center px-0.5 sm:px-2">
                              <span className="block text-[8px] sm:text-xs md:text-sm font-black tracking-wide uppercase leading-tight"
                                style={{ 
                                  textShadow: isActive 
                                    ? '0 0 15px rgba(255,255,255,0.6), 0 2px 12px rgba(0,0,0,0.9)'
                                    : '0 2px 6px rgba(0,0,0,0.9)'
                                }}
                              >
                                {displayName}
                              </span>
                            </div>
                            
                            {/* Badge Premium - TAILLE RÉDUITE MOBILE */}
                            {isActive && (
                              <div className="absolute -top-1.5 -right-1.5 sm:-top-3 sm:-right-3 w-6 h-6 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-2xl transform rotate-12 animate-bounce"
                                style={{
                                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6B00 100%)',
                                  boxShadow: '0 6px 15px rgba(255, 140, 0, 0.8), inset 0 -2px 4px rgba(0,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.7)',
                                  border: '1.5px solid rgba(255, 255, 255, 0.5)'
                                }}
                              >
                                <span className="text-sm sm:text-lg" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,1))' }}>✨</span>
                              </div>
                            )}
                            
                            {/* Bottom highlight line */}
                            {isActive && (
                              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-0.5 sm:h-1.5 rounded-full"
                                style={{ 
                                  background: 'linear-gradient(90deg, transparent, white, transparent)',
                                  boxShadow: '0 0 15px white'
                                }}
                              ></div>
                            )}
                          </div>
                        </div>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </div>

            {/* Articles Grid - SEULEMENT pour VENTE (pas TOURNOI) */}
            {activeTab === 'vente' && (
              <div className="mt-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredArticles.map(article => (
                  <Card
                    key={article.id}
                    data-testid={`article-${article.nom.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleAddToCart(article)}
                    className="relative overflow-hidden group cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border-2 border-transparent hover:border-[#C6A15B]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(28, 37, 65, 0.9) 0%, rgba(42, 59, 92, 0.8) 100%)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    
                    <CardContent className="p-5 text-center relative z-10">
                      {/* Icon/Image placeholder */}
                      <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#C6A15B] to-[#B89149] flex items-center justify-center text-3xl shadow-lg">
                        {selectedRubrique?.nom === 'JEUX' && '🎮'}
                        {selectedRubrique?.nom === 'BOISSON' && '🥤'}
                        {(selectedRubrique?.nom === 'CAFE' || selectedRubrique?.nom === 'CAFÉ' || selectedRubrique?.nom === 'NESPRESSO') && '☕'}
                        {selectedRubrique?.nom === 'CASINO' && '🎰'}
                        {!['JEUX', 'BOISSON', 'CAFE', 'CAFÉ', 'NESPRESSO', 'CASINO'].includes(selectedRubrique?.nom) && '📦'}
                      </div>
                      
                      <h3 className="font-bold text-[#EAEAEA] mb-3 text-sm md:text-base line-clamp-2">{article.nom}</h3>
                      
                      {article.prixUnit && (
                        <div className="bg-gradient-to-r from-[#C6A15B]/20 to-[#B89149]/20 rounded-lg p-2 border border-[#C6A15B]/30">
                          <p className="text-[#C6A15B] font-black text-lg">{article.prixUnit.toLocaleString()} <span className="text-sm">FCFA</span></p>
                        </div>
                      )}
                      {article.prixParDuree && (
                        <div className="bg-gradient-to-r from-[#3B82F6]/20 to-[#2563EB]/20 rounded-lg p-2 border border-[#3B82F6]/30">
                          <p className="text-[#3B82F6] font-bold text-sm">⏱️ Tarif horaire</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Tournoi Content - ULTRA PREMIUM */}
            {activeTab === 'tournoi' && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form */}
                <Card className="glass-effect border-[#EF4444]/30 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-orange-500/5 to-red-500/5 animate-pulse"></div>
                  
                  <CardContent className="p-6 relative z-10">
                    <div className="flex items-center gap-2 mb-6">
                      <Trophy className="w-6 h-6 text-[#EF4444]" />
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EF4444] to-[#DC2626]">Nouveau Tournoi</h2>
                    </div>

                    <form onSubmit={handleTournoiSubmit} className="space-y-4">
                      <div>
                        <Label className="text-[#EAEAEA]">Nom du Tournoi *</Label>
                        <Input
                          value={tournoiFormData.nomTournoi}
                          onChange={(e) => handleTournoiInputChange('nomTournoi', e.target.value)}
                          placeholder="Ex: Tournoi FIFA 2024"
                          className="bg-[#0B132B] border-[#EF4444]/30 text-[#EAEAEA]"
                        />
                      </div>

                      <div>
                        <Label className="text-[#EAEAEA]">Nombre de Joueurs *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={tournoiFormData.nombreJoueurs}
                          onChange={(e) => handleTournoiInputChange('nombreJoueurs', e.target.value)}
                          placeholder="Ex: 16"
                          className="bg-[#0B132B] border-[#EF4444]/30 text-[#EAEAEA]"
                        />
                      </div>

                      <div>
                        <Label className="text-[#EAEAEA]">Participation par Joueur (FCFA) *</Label>
                        <Input
                          type="number"
                          min="0"
                          value={tournoiFormData.participation}
                          onChange={(e) => handleTournoiInputChange('participation', e.target.value)}
                          placeholder="Ex: 5000"
                          className="bg-[#0B132B] border-[#EF4444]/30 text-[#EAEAEA]"
                        />
                      </div>

                      <div>
                        <Label className="text-[#EAEAEA]">Organisé par *</Label>
                        <Select value={tournoiCommercial} onValueChange={setTournoiCommercial}>
                          <SelectTrigger className="bg-[#0B132B] border-[#EF4444]/30 text-[#EAEAEA]">
                            <SelectValue placeholder="Sélectionner le commercial..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1C2541] border-[#EF4444]/30 text-[#EAEAEA]">
                            {commercials.map(commercial => (
                              <SelectItem key={commercial.id} value={commercial.id}>
                                {commercial.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        type="submit"
                        disabled={tournoiLoading}
                        className="w-full bg-gradient-to-r from-[#EF4444] to-[#DC2626] hover:from-[#DC2626] hover:to-[#B91C1C] text-white font-bold py-6 text-lg shadow-xl shadow-red-500/40 hover:scale-105 transition-all"
                      >
                        {tournoiLoading ? 'Enregistrement...' : '🏆 Enregistrer le Tournoi'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Preview */}
                <Card className="glass-effect border-[#C6A15B]/30 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 via-amber-500/5 to-yellow-500/5 animate-pulse"></div>
                  
                  <CardContent className="p-6 relative z-10">
                    <div className="flex items-center gap-2 mb-6">
                      <DollarSign className="w-6 h-6 text-[#C6A15B]" />
                      <h2 className="text-xl font-bold text-[#C6A15B]">Aperçu des Gains</h2>
                    </div>

                    {tournoiPreviewData ? (
                      <div className="space-y-4">
                        <div className="bg-[#0B132B] rounded-lg p-4 border-2 border-[#C6A15B]/30">
                          <p className="text-[#EAEAEA]/60 text-sm mb-1">Total du Tournoi</p>
                          <p className="text-3xl font-bold text-[#C6A15B]">
                            {tournoiPreviewData.total.toLocaleString()} FCFA
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 rounded-lg p-4 border-2 border-yellow-500/30">
                            <div className="flex items-center gap-2 mb-2">
                              <Trophy className="w-5 h-5 text-yellow-500" />
                              <p className="text-[#EAEAEA] font-semibold">Gain Vainqueur (40%)</p>
                            </div>
                            <p className="text-2xl font-bold text-yellow-400">
                              {tournoiPreviewData.gainVainqueur.toLocaleString()} FCFA
                            </p>
                          </div>

                          <div className="bg-gradient-to-br from-gray-400/10 to-gray-500/10 rounded-lg p-4 border-2 border-gray-400/30">
                            <div className="flex items-center gap-2 mb-2">
                              <Trophy className="w-5 h-5 text-gray-400" />
                              <p className="text-[#EAEAEA] font-semibold">Gain 2ème (20%)</p>
                            </div>
                            <p className="text-2xl font-bold text-gray-300">
                              {tournoiPreviewData.gainDeuxieme.toLocaleString()} FCFA
                            </p>
                          </div>

                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#C6A15B] rounded-xl blur-sm opacity-20"></div>
                            <div className="relative bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 border-2 border-[#FFD700] p-4 rounded-xl">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Users className="w-5 h-5 text-[#FFD700]" />
                                  <p className="text-[#FFD700] text-sm font-bold uppercase tracking-wider">PlayCity (40%)</p>
                                </div>
                                <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                                  {tournoiPreviewData.gainPlayCity.toLocaleString()} <span className="text-lg">FCFA</span>
                                </p>
                              </div>
                              <p className="text-[#EAEAEA]/60 text-xs mt-2">
                                💰 Ajouté au CA JEUX
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64">
                        <p className="text-[#EAEAEA]/60">
                          Remplissez les champs pour voir l'aperçu
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </Tabs>

          {/* Panier - En dessous des catégories sur mobile, à droite sur desktop */}
          {activeTab === 'vente' && (
            <div className="lg:max-w-md lg:mx-auto xl:max-w-none">
              <Card className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-[#EAEAEA] flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Panier
                    </h2>
                    {panier.length > 0 && (
                      <span className="bg-[#C6A15B] text-[#0B132B] px-2 py-1 rounded-full text-sm font-bold">
                        {panier.length}
                      </span>
                    )}
                  </div>

                  {panier.length === 0 ? (
                    <p className="text-[#EAEAEA]/60 text-center py-8">Panier vide</p>
                  ) : (
                    <div className="space-y-3">
                      {panier.map((item, index) => (
                        <div key={index} className="bg-[#1C2541] rounded-lg p-3 space-y-2">
                          <div className="flex items-start justify-between">
                            <p className="text-[#EAEAEA] text-sm font-medium flex-1">{item.libelle}</p>
                            <button
                              onClick={() => removeFromCart(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(index, -1)}
                                className="bg-[#0B132B] text-[#EAEAEA] w-7 h-7 rounded flex items-center justify-center hover:bg-[#C6A15B] hover:text-[#0B132B] transition"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="text-[#EAEAEA] font-bold w-8 text-center">{item.qte}</span>
                              <button
                                onClick={() => updateQuantity(index, 1)}
                                className="bg-[#0B132B] text-[#EAEAEA] w-7 h-7 rounded flex items-center justify-center hover:bg-[#C6A15B] hover:text-[#0B132B] transition"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-[#C6A15B] font-bold">{item.montant} FCFA</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {panier.length > 0 && (
                    <div className="mt-4 space-y-4">
                      <div className="border-t border-[#C6A15B]/30 pt-4">
                        <div className="flex items-center justify-between text-xl font-bold">
                          <span className="text-[#EAEAEA]">TOTAL</span>
                          <span className="text-[#C6A15B]">{calculateTotal().toLocaleString()} FCFA</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#EAEAEA]">Mode de paiement</Label>
                        <Select value={modePaiement} onValueChange={setModePaiement}>
                          <SelectTrigger className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
                            <SelectItem value="Especes">Espèces</SelectItem>
                            <SelectItem value="OrangeMoney">Orange Money</SelectItem>
                            <SelectItem value="MTNMoMo">MTN MoMo</SelectItem>
                            <SelectItem value="Wave">Wave</SelectItem>
                            <SelectItem value="Carte">Carte</SelectItem>
                            <SelectItem value="Autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        data-testid="encaisser-btn"
                        onClick={handleEncaisser}
                        disabled={loading}
                        className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B] font-bold py-6 text-lg"
                      >
                        {loading ? 'Encaissement...' : 'Encaisser'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Client Dialog */}
      <Dialog open={showClientDialog} onOpenChange={(open) => {
        setShowClientDialog(open);
        if (!open) {
          // Ne pas réinitialiser le client quand on ferme le dialog
          // Car on veut garder les infos pour l'encaissement
        }
      }}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B] flex items-center gap-2">
              <User className="w-5 h-5" />
              Informations Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                data-testid="client-nom-input"
                value={client.nom}
                onChange={(e) => setClient({ ...client, nom: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                autoComplete="off"
              />
            </div>
            <div>
              <Label>Prénom</Label>
              <Input
                value={client.prenom}
                onChange={(e) => setClient({ ...client, prenom: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                autoComplete="off"
              />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input
                data-testid="client-telephone-input"
                value={client.telephone}
                onChange={(e) => setClient({ ...client, telephone: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="text-[#EAEAEA] font-medium">Commercial Responsable *</Label>
              <Select value={selectedCommercial} onValueChange={setSelectedCommercial}>
                <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                  <SelectValue placeholder="Sélectionner le commercial..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
                  {commercials.map(commercial => (
                    <SelectItem key={commercial.id} value={commercial.id}>
                      {commercial.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasJeuxInCart() && (
              <div>
                <Label>Jeux *</Label>
                <Select
                  value={client.jeux}
                  onValueChange={(value) => setClient({ ...client, jeux: value })}
                  required
                >
                  <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                    <SelectValue placeholder="Sélectionner un jeu..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] max-h-64 overflow-y-auto">
                    {jeux.map(jeu => (
                      <SelectItem key={jeu.id} value={jeu.nom} className="hover:bg-[#C6A15B]/20">
                        {jeu.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              data-testid="confirm-client-btn"
              onClick={() => {
                if (!client.nom || !client.telephone) {
                  toast.error('Nom et téléphone requis');
                  return;
                }
                if (!selectedCommercial) {
                  toast.error('Veuillez sélectionner le commercial responsable');
                  return;
                }
                if (hasJeuxInCart() && !client.jeux) {
                  toast.error('Veuillez sélectionner le jeu');
                  return;
                }
                setShowClientDialog(false);
                // Call handleEncaisser directly
                setTimeout(() => handleEncaisser(), 100);
              }}
              disabled={loading}
              className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
            >
              {loading ? 'Encaissement...' : 'Continuer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Dialog */}
      <Dialog open={showPriceDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPriceDialog(false);
          setCurrentArticle(null);
          setCustomPrice('');
          setSelectedDuration(null);
        }
      }}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B]">
              {selectedRubrique?.type === 'LIBRE' ? 'Saisir le prix' : 'Sélectionner la durée'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedRubrique?.type === 'LIBRE' ? (
              <div>
                <Label>Prix (FCFA)</Label>
                <Input
                  type="number"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  placeholder="Entrer le prix"
                />
              </div>
            ) : (
              <div>
                <Label className="text-[#EAEAEA] mb-2 block">Durée</Label>
                <div className="grid grid-cols-2 gap-3">
                  {(() => {
                    // CEO : source de vérité = clés de prixParDuree de l'article (PS4/PS5/Meta Quest 3/PS VR2 peuvent avoir des tranches différentes)
                    // CEO Session 5 : Filtrer les tranches désactivées (disabledDurees)
                    // Fallback : si l'article n'a pas prixParDuree, on retombe sur echellesTemps de la rubrique (comportement existant)
                    const disabledSet = new Set((currentArticle?.disabledDurees) || []);
                    const articleDurations = currentArticle?.prixParDuree
                      ? Object.keys(currentArticle.prixParDuree)
                          .filter(k => !disabledSet.has(k))
                          .map(k => parseInt(k, 10))
                          .sort((a, b) => a - b)
                      : (selectedRubrique?.echellesTemps || []);
                    return articleDurations.map(duree => {
                      const prix = currentArticle?.prixParDuree?.[duree.toString()] || 0;
                      const sub = getDureeSubLabel(duree);
                      return (
                        <button
                          key={duree}
                          onClick={() => setSelectedDuration(duree.toString())}
                          className={`p-4 rounded-lg border-2 transition ${
                            selectedDuration === duree.toString()
                              ? 'border-[#C6A15B] bg-[#C6A15B]/20'
                              : 'border-[#C6A15B]/30 bg-[#0B132B]'
                          }`}
                        >
                          <div className="text-[#EAEAEA] font-bold">
                            {formatDuree(duree)}
                            {sub && <span className="text-[#93C5FD] text-xs font-normal ml-1">({sub})</span>}
                          </div>
                          <div className="text-[#C6A15B] text-sm">
                            {prix.toLocaleString()} FCFA
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
            <Button
              onClick={handleConfirmPrice}
              className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
            >
              Ajouter au panier
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B]">Reçu de vente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {receiptImage && (
              <div className="bg-white rounded-lg p-4">
                <img src={receiptImage} alt="Reçu" className="w-full" />
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={async () => {
                  if (receiptImage) {
                    try {
                      // Convert base64 to blob for sharing
                      const base64Response = await fetch(receiptImage);
                      const blob = await base64Response.blob();
                      const file = new File([blob], 'recu_playcity.png', { type: 'image/png' });

                      // Check if Web Share API is supported (mobile devices)
                      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({
                          files: [file],
                          title: 'Reçu PlayCity Immersion',
                          text: 'Voici votre reçu PlayCity Immersion'
                        });
                        toast.success('Reçu partagé avec succès !');
                      } else {
                        // Fallback: Download and open WhatsApp
                        const link = document.createElement('a');
                        link.href = receiptImage;
                        link.download = `recu_playcity.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        toast.success('Image téléchargée ! Veuillez la partager manuellement sur WhatsApp');
                        
                        // Open WhatsApp with a generic message (will open app on mobile)
                        setTimeout(() => {
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent('Voici votre reçu PlayCity Immersion. L\'image a été téléchargée dans votre galerie.')}`;
                          window.open(whatsappUrl, '_blank');
                        }, 500);
                      }
                    } catch (error) {
                      console.error('Erreur lors du partage:', error);
                      toast.error('Erreur lors du partage. Téléchargement en cours...');
                      
                      // Fallback: simple download
                      const link = document.createElement('a');
                      link.href = receiptImage;
                      link.download = `recu_playcity.png`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                Partager via WhatsApp
              </Button>
              <Button
                onClick={() => setShowReceiptDialog(false)}
                className="flex-1 bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
              >
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vendre;
